import type { StepStatus } from '@/lib/journey'
import type { ProcedureStatus } from '@/types/cgt'
import type { KitRow } from '@/types/pickList'

/** The part of a kit row that decides a kit run's progress. */
export type KitProgress = Pick<KitRow, 'buildStatus' | 'started' | 'completedForms' | 'totalForms'>

/**
 * A kit RUN's progress from its kits (z8nrz7e11d).
 *
 * The kit lists are one row per kit — per kit form — so a run with two kit
 * forms is two rows. The run is ready only when every one of its kits is
 * COMPLETED and every kit form is done (a form not yet started has no row, so
 * the form counts guard that case); started once any kit is. Pass both feeds —
 * in progress and finished — in either order; a kit in both reads as finished.
 */
export function kitProgressByProcedure(rows: readonly KitRow[]): Map<string, KitProgress> {
  const kits = new Map<string, Map<string, KitRow>>()
  for (const r of rows) {
    const key = r.buildId ?? `run:${r.procedureId}`
    const mine = kits.get(r.procedureId) ?? new Map<string, KitRow>()
    const seen = mine.get(key)
    if (!seen || r.buildStatus === 'COMPLETED') mine.set(key, r)
    kits.set(r.procedureId, mine)
  }
  const out = new Map<string, KitProgress>()
  for (const [procedureId, mine] of kits) {
    const list = [...mine.values()]
    const completedForms = Math.max(...list.map(r => r.completedForms))
    const totalForms = Math.max(...list.map(r => r.totalForms))
    const allDone = list.every(r => r.buildStatus === 'COMPLETED') && completedForms >= totalForms
    out.set(procedureId, {
      buildStatus: allDone ? 'COMPLETED' : list.some(r => r.buildStatus) ? 'IN_PROGRESS' : null,
      started: list.some(r => r.started),
      completedForms,
      totalForms,
    })
  }
  return out
}

export interface StepStatusInput {
  procedureStatus: ProcedureStatus
  completed: number
  total: number
  /** A step has a draft or active submission. */
  hasActive: boolean
  /** A form of the run is completed and waiting for its reviews (z8nrz7dt41). */
  hasPendingReview?: boolean
  /** Present only for a kit run. */
  kit?: KitProgress | null
}

export interface DerivedStepStatus {
  completed: number
  total: number
  stepStatus: StepStatus
  stepLabel: string
}

/**
 * The progress and status a procedure row shows, shared by the procedures
 * tables and the Overall Status board so both apply the same rules.
 *
 * The counts passed in can still differ between the two: the Transplants table
 * scores a patient's steps across all their runs of a template, while the board
 * scores each run on its own.
 *
 * A KIT's progress and status come from its BUILD, never from step progress.
 * A kit has no patient, so its step progress resolves to nothing, and nothing
 * advances a kit procedure past `in_progress`: the KitBuild records completion.
 * `buildStatus` is the same authority Kit Inventory keys on. The build reaches
 * COMPLETED at End ("Kitted by"), when the kit physically exists.
 *
 * With no step data at all, the procedure's own status is the fallback, so a
 * completed or cancelled run does not read "Not started".
 */
export function deriveProcedureStepStatus({
  procedureStatus,
  completed,
  total,
  hasActive,
  hasPendingReview = false,
  kit,
}: StepStatusInput): DerivedStepStatus {
  // Before everything else, kits included: whatever kind of run it is, a form
  // waiting for its reviews holds it up, and that is what it should say. A
  // cancelled run stays cancelled.
  if (hasPendingReview && procedureStatus !== 'cancelled') {
    return {
      completed: kit ? kit.completedForms : completed,
      total: kit ? kit.totalForms : total,
      stepStatus: 'pending_review',
      stepLabel: 'Pending review',
    }
  }
  if (kit) {
    const done = kit.buildStatus === 'COMPLETED'
    return {
      completed: kit.completedForms,
      total: kit.totalForms,
      stepStatus: done ? 'completed' : kit.started ? 'in_progress' : 'not_started',
      stepLabel: done
        ? 'Kit ready'
        : kit.started
          ? kit.totalForms > 0
            ? `${kit.completedForms}/${kit.totalForms} forms done`
            : 'In progress'
          : 'Not started',
    }
  }
  const counts = { completed, total }
  if (total > 0 && completed === total) return { ...counts, stepStatus: 'completed', stepLabel: 'Complete' }
  if (completed > 0 || hasActive) {
    return {
      ...counts,
      stepStatus: 'in_progress',
      stepLabel: total > 0 ? `${completed}/${total} steps done` : 'In progress',
    }
  }
  if (total === 0) {
    if (procedureStatus === 'completed') return { ...counts, stepStatus: 'completed', stepLabel: 'Complete' }
    if (procedureStatus === 'cancelled') return { ...counts, stepStatus: 'locked', stepLabel: 'Cancelled' }
    if (procedureStatus === 'in_progress') return { ...counts, stepStatus: 'in_progress', stepLabel: 'In progress' }
  }
  return { ...counts, stepStatus: 'not_started', stepLabel: 'Not started' }
}
